'use client';
import type { BlockId, ComputedVisibilityPayloadType } from '@unifyapps/defs/types/block';
import type { ConditionEvaluationObject } from '@unifyapps/defs/types/conditionEvaluationObject';
import { useEffect, useRef, useState } from 'react';
import InterfacePageHelper from '../../helper/InterfacePage';
import { useGlobalAppStateStore } from '../../stores/app/GlobalAppStateStore';
import {
  getActiveInterfacePage,
  useGetBuilderInterfaceStoreState,
} from '../../stores/InterfaceStore/builder';
import { isEmptyBlock } from '../../utils/block';
import type { ComputeContextType } from '../computeContext/context/types';
import { useEvaluateConditionalOperator } from '../useComputeConditionEvaluationGetter';

export const isTargetVisible = (visibility: ComputedVisibilityPayloadType | undefined) => {
  if (!visibility) return true;

  if (visibility.value === 'conditions') {
    return visibility.conditions !== false;
  }

  return visibility.value !== false;
};

const DEBOUNCE_DELAY = 300;

function useIsBlockVisible(blockId: BlockId, context: ComputeContextType) {
  const { evaluateConditionalOperator } = useEvaluateConditionalOperator();
  const { getBuilderInterfaceStoreState } = useGetBuilderInterfaceStoreState();
  const { device } = useGlobalAppStateStore().hooks.useDeviceDetails();
  const [isVisible, setIsVisible] = useState(true);
  const timerRef = useRef<number | null>(null);

  // cache to avoid recomputation when visibility and context haven't changed
  const cacheRef = useRef<{
    visibilityConditions: ConditionEvaluationObject | undefined;
    context: ComputeContextType;
    result: boolean;
  } | null>(null);

  useEffect(() => {
    if (isEmptyBlock(blockId)) {
      setIsVisible(true);
      return;
    }

    const activeInterfacePage = getActiveInterfacePage(getBuilderInterfaceStoreState());
    if (!activeInterfacePage) {
      setIsVisible(true);
      return;
    }

    const block = InterfacePageHelper.getBlockFromInterfacePage(
      activeInterfacePage,
      device,
      blockId,
    );
    const visible = block?.visibility;

    // early returns for simple cases
    if (!visible) {
      setIsVisible(true);
      return;
    }
    if (visible.value !== 'conditions') {
      setIsVisible(visible.value !== false);
      return;
    }

    // check cache for same visibility and context references
    if (
      cacheRef.current?.visibilityConditions === visible.conditions &&
      cacheRef.current?.context === context
    ) {
      setIsVisible(cacheRef.current.result);
      return;
    }

    // clear existing timer
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      const result =
        evaluateConditionalOperator({
          conditionEvaluationObj: visible.conditions,
          context,
        }) !== false;

      // cache the result with references
      cacheRef.current = { visibilityConditions: visible.conditions, context, result };
      setIsVisible(result);
    }, DEBOUNCE_DELAY) as unknown as number;

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [blockId, context, getBuilderInterfaceStoreState, device, evaluateConditionalOperator]);

  return isVisible;
}

export default useIsBlockVisible;
